import { extractErrorMessage } from './errorUtils';

/**
 * Open a blob in a new browser tab via an anchor click
 * (avoids false popup-blocker failures from window.open).
 */
export const openBlobInNewTab = (blob: Blob, fallbackMimeType = 'application/pdf') => {
  const typedBlob =
    blob.type && blob.type !== 'application/octet-stream'
      ? blob
      : new Blob([blob], { type: fallbackMimeType });

  const url = URL.createObjectURL(typedBlob);
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** Trigger a file download for a blob. */
export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Resolve a user-facing message from an axios blob error response
 * (APIs often return JSON error payloads with responseType: 'blob').
 */
export async function resolveBlobErrorMessage(error: unknown, fallback: string): Promise<string> {
  const axiosErr = error as { response?: { data?: Blob }; message?: string };
  const errorBlob = axiosErr?.response?.data;

  if (errorBlob instanceof Blob) {
    try {
      const text = await errorBlob.text();
      const parsed = JSON.parse(text) as {
        data?: { Error?: string; error?: string };
        message?: string;
        Message?: string;
      };
      return (
        parsed.data?.Error ||
        parsed.data?.error ||
        parsed.message ||
        parsed.Message ||
        extractErrorMessage(parsed, text || fallback)
      );
    } catch {
      // Not JSON — fall through
    }
  }

  return extractErrorMessage(error, fallback);
}
